import React, { useContext, useEffect, useReducer, useState, useLayoutEffect } from 'react';

import {
  TextField,
  Button,
  AppHeader,
  Typography,
  Icon,
  IconButton,
  Stack,
  AppHeaderLogoIcon,
  Divider,
  Box,
} from '@ace-clublabs/shared-component-library';
import { useHistory } from 'react-router-dom';
import { Pages } from '@lib/types';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { registrationStatusReducer } from '../application/reducers/registrationDetailsReducers';
import {
  generateRegistrationStatus,
  mapRegistrationDataOntoSessionData,
} from '../application/viewmodels/RegistrationFormStatus';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { getLogInPage } from '../utils/helpers';
import { validateMembershipFormData } from '../utils/validations';
import { FlowType, MembershipLevel, MembershipProductType } from '@ace-clublabs/common-membership-api-types';
import { NewJoinScrollView } from '../components/ScrollView';
import { UtagHelper, UtagType } from '../utils/UtagHelper';
import tmobile_logo from '../assets/T-Mobile-Logo.png';
import { DownloadAppSection } from '../components/ConfirmationPageComponents/DownloadAppSection';
import { DisclaimerComponent } from '../components/ConfirmationPageComponents/DisclaimerComponent';
import { MembershipOverviewSection } from '../components/ConfirmationPageComponents/MembershipOverviewSection';
import { GiftFlowOverviewSection } from '../components/ConfirmationPageComponents/GiftFlowOverviewSection';
import { PartnerEnrollmentStatusBanner } from '../components/ConfirmationPageComponents/PartnerEnrollmentStatusBanner';
import { ConfirmationTitle } from '../components/ConfirmationPageComponents/ConfirmationTitle';

const handleLoginClick = () => {
  const url = getLogInPage(window.location.hostname);
  window.open(url);
};

const handleRoadsideClick = () => {
  const newTabbedWindow = window.open('', '_blank');
  (newTabbedWindow as Window).location = 'https://chat.ace.aaa.com/';
};

export const ConfirmationPage: React.FC = () => {
  const {
    sessionData,
    registerIdentityAccount,
    loadingInfo,
    updateMembershipForm,
    sendTag,
    membershipInfo,
    refreshSession,
  } = useContext(MembershipAppStateContext);
  const confirmationInfo = sessionData.confirmationData;
  const completeRegistration = sessionData.completedRegistration;
  const completedPartnerEnrollment = sessionData.completedPartnerEnrollment;

  const hasAvailablePartnerOffers = sessionData.partnerOffers?.availableOffers?.length! > 0;

  const flowType = sessionData.flowType;
  const isGiftFlow = sessionData.flowType === FlowType.GIFT;
  const isMajorRocketFlow = sessionData.flowType === FlowType.MAJOR_ROCKET;
  const isGrouponFlow = sessionData.flowType === FlowType.GROUPON;
  const history = useHistory();
  const [enterNewEmail, setEnterNewEmail] = useState(false);
  const [displayErrorMessage, setDisplayErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isTagging, setIsTagging] = useState(true);
  const [clickedEmailEditButton, setEmailEditButtonClicked] = useState(false);

  const [registrationForm, dispatchRegistrationFormUpdate] = useReducer(
    registrationStatusReducer,
    generateRegistrationStatus(sessionData?.formData)
  );

  const ersFee = membershipInfo?.membershipCost?.fees?.sameDayServiceFee;
  const isEnrolledInAutoPay = confirmationInfo?.autoPayStatus === 'succeeded';
  const purchasedMembershipLevel = confirmationInfo?.membershipLevel;

  const ableToBuyErs = membershipInfo?.membershipProducts?.ERS?.includes(purchasedMembershipLevel as MembershipLevel);
  const boughtErs = confirmationInfo?.options?.includes(MembershipProductType.ERS);
  const custID = confirmationInfo?.confirmationId?.slice(0, 15);

  const numOfAssociates =
    confirmationInfo?.members && Array.isArray(confirmationInfo.members) ? confirmationInfo.members.length : 0;
  const optionsAdded =
    confirmationInfo?.options && Array.isArray(confirmationInfo.options) && confirmationInfo.options.length;
  const rvAdded = confirmationInfo?.options && confirmationInfo.options.includes(MembershipProductType.RV);
  const isImmediateRoadsideAssistanceSelected = confirmationInfo?.options?.some(
    (val) => val === MembershipProductType.ERS
  );

  const handleEmailEditClick = (e: any) => {
    e?.preventDefault();
    setEnterNewEmail(true);
    setEmailEditButtonClicked(true);
  };

  const mem_associate = numOfAssociates.toString();
  const mem_benefitlevel = 'MBRSHP_BASIC'; //TODO is this dynamic
  const mem_ers = boughtErs ? 'true' : ableToBuyErs ? 'false' : 'na';
  const mem_primary = '1';
  const { transaction_id, confirmation_id, ins_quoteid } = confirmationInfo?.tags || {};
  const autoPayTag = isEnrolledInAutoPay ? 'olm enroll' : '';

  let tagOverrides = {};
  if (isGiftFlow) {
    tagOverrides = {
      custID: custID,
      confirmation_id,
      mem_associate,
      transaction_id,
      transaction_type: confirmationInfo?.paymentDetails?.paymentType?.replaceAll(' ', '')?.toLowerCase(), // this will not be mapped
      ins_quoteid,
      ...UtagHelper.buildProductTags(sessionData),
    };
  } else {
    tagOverrides = {
      custID: custID,
      confirmation_id,
      mem_associate,
      transaction_id,
      transaction_type: confirmationInfo?.paymentDetails?.paymentType?.replaceAll(' ', ''), // this will not be mapped
      autopay: autoPayTag,
      mem_benefitlevel,
      mem_ers,
      mem_primary,
      mem_transactiontype: 'MBRSHP_NEW',
      ins_quoteid,
      total: confirmationInfo?.amount?.toString(),
      ...UtagHelper.buildProductTags(sessionData),
    };
  }

  useEffect(() => {
    const updatedForm = mapRegistrationDataOntoSessionData(registrationForm, sessionData.formData);
    updateMembershipForm(updatedForm);
  }, [registrationForm]);

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo?.benefits) {
      return;
    }

    if (!completeRegistration) {
      const tagPage = sessionData.flowType === FlowType.GIFT ? 'membership:gift:confirmation' : 'confirmation';
      sendTag(UtagType.view, tagPage, { ...tagOverrides });
    } else if (hasAvailablePartnerOffers && completedPartnerEnrollment) {
      const tagPage = 'partner confirmation';
      const partnerEnrollmentsTags = UtagHelper.buildPartnerEnrollmentFinalSelectionTags(sessionData, membershipInfo);
      const partnerEnrollmentErrorTags = UtagHelper.buildPartnerEnrollmentErrorStatusTags(sessionData);
      sendTag(UtagType.view, tagPage, { ...partnerEnrollmentsTags, ...partnerEnrollmentErrorTags });
    }
    setIsTagging(false);
  }, [membershipInfo]);

  const registerAccount = async () => {
    const registrationErrors = validateMembershipFormData(sessionData.formData)['registration'];
    if (!registrationErrors) {
      try {
        const sessionId = sessionData?.sessionId;
        if (sessionId == null) {
          throw 'Session does not exist.';
        }

        const response = await registerIdentityAccount(
          sessionId,
          registrationForm.email.value!,
          registrationForm.password.value!
        );

        if (response?.success == true) {
          sendTag(UtagType.link, 'confirmation-membership registration success', {
            ...UtagHelper.buildRegistrationTags(sessionData, membershipInfo!),
            cta: 'membership:join:membership registration complete',
          });
          setEnterNewEmail(false);
          setDisplayErrorMessage(false);
          history.push(Pages.PartnerEnrollment);
        } else {
          // Handle error here
          sendTag(UtagType.link, 'confirmation-membership registration error', {
            ...UtagHelper.buildRegistrationTags(sessionData, membershipInfo!),
            error: `membership:join:membership registration-${response?.error_code
              ?.toLowerCase()
              ?.replaceAll('_', ' ')}`,
          });
          setEnterNewEmail(true);
          setDisplayErrorMessage(true);
          const errorMessage =
            response?.error_code === 'email_in_use'
              ? 'Please enter a different email address. This email address will be linked to your new membership.'
              : 'Unable to register account. Please try again.';
          setErrorMessage(errorMessage);
        }
      } catch (error: any) {
        history.push(Pages.SorryPage);
      }
    } else {
      dispatchRegistrationFormUpdate({ field: 'registrationForm', value: null });
    }
  };

  const registerAccountSection = (
    <div className="register-account-container" hidden={completeRegistration}>
      <div data-di-mask="true" className="register-inputs-list qm-mask">
        <Typography variant="b1Medium" className="register-account-title">
          Finish creating your account
        </Typography>
        {displayErrorMessage ? (
          <Typography variant="b3Regular" className="enter-new-email" sx={{ color: 'red' }}>
            {errorMessage}
          </Typography>
        ) : null}
        {enterNewEmail ? (
          <TextField
            label="Email"
            validationErrorMessage={registrationForm.email.errorMessage}
            type="text"
            error={registrationForm.email.error}
            showClearButton={true}
            onChange={(v) => {
              dispatchRegistrationFormUpdate({
                field: 'email',
                value: v.currentTarget.value,
              });
            }}
            onClearButtonClick={() => {
              dispatchRegistrationFormUpdate({
                field: 'email',
                value: '',
              });
            }}
            defaultValue={clickedEmailEditButton ? registrationForm.email.value : ''}
            id="emailAddress"
            fullWidth
            sx={{
              '& #emailAddress.Mui-disabled': {
                WebkitTextFillColor: '#707070',
                color: '#707070',
                backgroundColor: '#FFFFFF',
              },
              '& .MuiInputLabel-root.Mui-disabled': {
                color: '#404040',
              },
              '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
                borderColor: '#D0D0D0',
              },
            }}
          />
        ) : (
          <Stack direction="row" justifyContent="space-between" alignItems="center" className="email-text-display">
            <Typography variant="b1Regular" id="email-address" className="email-address">
              Email: {registrationForm?.email?.value ?? ''}
            </Typography>
            <IconButton
              aria-label="edit email"
              onClick={handleEmailEditClick}
              id="edit-email-button"
              sx={{ fontSize: '1rems' }}
            >
              <Icon iconName="pen-regular" />
            </IconButton>
          </Stack>
        )}

        <Typography id="email-username-disclaimer" sx={{ fontSize: '16px' }}>
          Your email address will also be the username to log in to your account.
        </Typography>

        <TextField
          value={registrationForm.password.value}
          label="Create password"
          id="createPassword"
          type="password"
          fullWidth
          required
          error={registrationForm.password.error}
          validationErrorMessage={registrationForm.password.errorMessage}
          onChange={(v) => {
            dispatchRegistrationFormUpdate({
              field: 'password',
              value: v.currentTarget.value,
              data: { email: registrationForm.email.value },
            });
            if (registrationForm.confirmPassword.value) {
              dispatchRegistrationFormUpdate({
                field: 'confirmPassword',
                value: registrationForm.confirmPassword.value,
                data: { password: v.currentTarget.value },
              });
            }
          }}
          sx={{ paddingTop: '12px' }}
        />
        <div className="password-requirement-section">
          <Typography className="password-requirement-title">Password requirements:</Typography>
          <ul>
            <li>Case sensitive</li>
            <li>Be 8-31 characters</li>
            <li>Include at least 1 number and 1 letter</li>
            <li>Can include: {'^ - ! @ # { } ~ $ _ .'} </li>
            <li>Cannot contain the word "password"</li>
            <li>Cannot be your email</li>
          </ul>
        </div>

        <TextField
          value={registrationForm.confirmPassword.value}
          label="Confirm password"
          id="confirmPassword"
          type="password"
          fullWidth
          required
          error={registrationForm.confirmPassword.error}
          validationErrorMessage={registrationForm.confirmPassword.errorMessage}
          onChange={(v) => {
            dispatchRegistrationFormUpdate({
              field: 'confirmPassword',
              value: v.currentTarget.value,
              data: { password: registrationForm.password.value },
            });
          }}
        />
        <Button
          label="Complete registration"
          fullWidth
          onClick={registerAccount}
          variant="contained"
          size="small"
          id="registerButton"
        ></Button>
      </div>
    </div>
  );

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null;
  if (isLoading) {
    return (
      <div>
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator maxSteps={3} currentStep={loadingInfo.loadingDetails.length} loadingInfo={loadingInfo} />
      </div>
    );
  }

  if (isTagging) {
    return <></>;
  }

  return (
    <>
      <div className="confirmation-page-view">
        {flowType === FlowType.T_MOBILE ? (
          <AppHeader
            sx={{ boxShadow: '' }}
            logo={
              <Stack direction={'row'} gap={'1rem'} alignItems={'center'} id="t-mobile-header">
                <AppHeaderLogoIcon iconName="ace-orbit-logo" />
                {/* @ts-ignore */}
                <Divider orientation="vertical" />
                <Box className="t-mobile-logo-container">
                  <img src={tmobile_logo} alt="t-mobile logo" />
                </Box>
              </Stack>
            }
          />
        ) : (
          <AppHeader sx={{ padding: 0 }} />
        )}
        <ConfirmationTitle />
        {isMajorRocketFlow || isGrouponFlow ? (
          <div className="confirm-text confirm-text-prior-registration" hidden={completeRegistration}>
            <Typography variant="b1Regular">Your one-time AAA membership offer has been redeemed.</Typography>
            <br />
            {isMajorRocketFlow && (
              <Typography variant="b1Regular">
                Once you complete your online My Account registration below, you can add household members, upgrade your
                benefit level, and enroll in paperless.
              </Typography>
            )}
          </div>
        ) : (
          <div hidden={completeRegistration}>
            {isGiftFlow ? (
              <></>
            ) : (
              <Typography variant="b1Regular" className="confirm-text confirm-text-prior-registration">
                You will be receiving additional membership information by email.
              </Typography>
            )}
          </div>
        )}

        <div hidden={!completeRegistration}>
          <Typography
            variant="b1Regular"
            className="confirm-text confirm-text-after-registration"
            sx={{ marginBottom: '18px' }}
          >
            {isMajorRocketFlow ? (
              <>
                Now that you’ve completed your My Account registration, login to add household members, upgrade your
                benefit level, and enroll in paperless.
              </>
            ) : (
              <></>
            )}
          </Typography>
          <PartnerEnrollmentStatusBanner />
          <Stack
            direction={'row'}
            flexWrap="wrap"
            className="roadside-login-cta-stack"
            gap="0.5rem"
            marginBottom="1rem"
          >
            <Button
              label="Roadside"
              onClick={() => {
                sendTag(
                  UtagType.link,
                  'partner confirmation',
                  UtagHelper.buildOnClickRoadsideTags(sessionData, membershipInfo)
                );
                handleRoadsideClick();
              }}
              variant="contained"
              size="small"
              id="roadsideButton"
              startIcon={<Icon iconName="truck-duotone" />}
              rounded
              sx={{
                background: '#DC4731',
                border: 'none',
                ':hover': { background: '#B3321E' },
              }}
            ></Button>
            <Button
              label="Log in"
              onClick={handleLoginClick}
              variant="outlined"
              size="small"
              id="loginButton"
              startIcon={<Icon iconName="user-duotone" />}
              rounded
              sx={{
                marginTop: '0rem !important',
                marginBottom: '0rem !important',
              }}
            ></Button>
          </Stack>
          {!isMajorRocketFlow && (
            <Stack className="scroll-view-section" marginTop="1rem">
              <NewJoinScrollView />
            </Stack>
          )}
          {!isGiftFlow && (
            <Stack className="download-app-section-stack" marginTop="1rem">
              <DownloadAppSection />
            </Stack>
          )}
        </div>
        {isGiftFlow ? (
          <GiftFlowOverviewSection sessionData={sessionData} numOfAssociates={numOfAssociates} rvAdded={rvAdded} />
        ) : (
          <>
            {registerAccountSection}
            <MembershipOverviewSection
              confirmationInfo={confirmationInfo}
              numOfAssociates={numOfAssociates}
              optionsAdded={optionsAdded}
              isEnrolledInAutoPay={isEnrolledInAutoPay}
            />
            <Stack className="membership-overview-terms" direction="column" gap={'1rem'}>
              <DisclaimerComponent
                clubCode={sessionData.clubCode!}
                isERSSelected={isImmediateRoadsideAssistanceSelected}
                ersFee={ersFee}
              />
              {isEnrolledInAutoPay && (
                <Typography variant="b3Regular" sx={{ color: '#404040' }}>
                  You have elected to enroll into automatic payment. You will be charged automatically when your
                  membership renews.
                </Typography>
              )}
            </Stack>
            {!isGiftFlow && <DownloadAppSection completeRegistration={completeRegistration} />}
          </>
        )}
      </div>
    </>
  );
};
